<script setup>
/**
 * One task, and what a form built from it would look like.
 *
 * ── THE TWO LISTS ARE THE WHOLE POINT ─────────────────────────────────────────────
 *
 * A form is the task's LINEAR PREFIX — the straightforward questions it always asks, in order.
 * Everything else stays in the chat: a step whose choices are only known at the moment, one that
 * calls one of her own systems, one that hands the conversation to a person, anything after a
 * branch. So the preview shows BOTH lists side by side, each stay-in-chat step with the reason
 * the builder gave.
 *
 * Showing only the fields would be the tempting version and it would mislead: an owner would
 * read "four boxes" as "the whole task is a form now" and expect the assistant to stop asking in
 * the chat. It does not, and it must not.
 *
 * ── THE PUBLISH BUTTON IS DISABLED, NOT HIDDEN, WHEN THE NUMBER IS NOT READY ──────
 *
 * Hidden reads as "this feature does not exist"; disabled beside the verdict above reads as
 * "not yet, and here is why". The server refuses it either way — the controller asks the form
 * builder for the verdict again before it touches Meta — so this is presentation, never the gate.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import {
  FORM_LANGS,
  FORM_STATE,
  NOTHING_PUBLISHED,
  isStale,
  nothingPublished,
} from '../helpers/form-standing.js';
import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  task: { type: Object, required: true },
  /** The preview, once read. `null` while it has not been asked for. */
  form: { type: Object, default: null },
  isPreviewing: { type: Boolean, default: false },
  isPublishing: { type: Boolean, default: false },
  canPublish: { type: Boolean, default: false },
  /**
   * One already-translated sentence saying why publishing is off, or ''. Shown beside the row's
   * Publish buttons only while they are off, and only when there is one to show.
   */
  blockedReason: { type: String, default: '' },
  /** One already-translated sentence, or ''. */
  errorMessage: { type: String, default: '' },
  publishedNote: { type: String, default: '' },
  /**
   * What is live per language (P-4.8), from `formStanding`. `null` means the tab could not
   * check, and then nothing may be offered as if it were not live. The default is "nothing
   * published", which is exactly the row as it was before.
   */
  standing: { type: Object, default: () => NOTHING_PUBLISHED },
  /** The language whose "Try it" is opening, or ''. */
  tryingLang: { type: String, default: '' },
});

const emit = defineEmits(['preview', 'publish', 'hide', 'try', 'arrange']);

const { t, locale } = useWasloI18n();
const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const isOpen = computed(() => props.form !== null);

const shapeLabel = shape => {
  const key = `WASLO.FORMS.SHAPE.${String(shape ?? '').toUpperCase()}`;
  const word = t(key);
  return word === key ? t('WASLO.FORMS.SHAPE.UNTOLD') : word;
};

const stayReason = reason => {
  const key = `WASLO.FORMS.STAY.${String(reason ?? '').toUpperCase()}`;
  const sentence = t(key);
  return sentence === key ? t('WASLO.FORMS.STAY.UNTOLD') : sentence;
};

/**
 * One box on the form, as the CUSTOMER will read it.
 *
 * This line used to render `field.name` — the sidecar's sanitised machine key (`studio_choice`),
 * which appears on no form anywhere. So the one screen built to show an owner her form showed
 * her something else entirely, and a question WhatsApp had cut from 46 characters to 20 looked
 * perfect on it. The label is the server's, rendered and already capped; `name` survives only as
 * the fallback for an answer from a build that predates it.
 */
const fieldLine = field => {
  const line = t('WASLO.FORMS.TASKS.STEP_SHAPE', {
    name: field.label || field.name,
    shape: shapeLabel(field.shape),
  });
  // The truncation flag is the server's too. Comparing lengths here would be a second copy of a
  // cap that differs per box and trims before its ellipsis — right until it was not.
  return field.labelTruncated
    ? `${line} · ${t('WASLO.FORMS.TASKS.FIELD_CUT')}`
    : line;
};

const fields = computed(() => props.form?.fields ?? []);
const stays = computed(() => props.form?.stayInChat ?? []);

// "2 of 10" when the builder told us its cap, plain "2" when it did not. The cap is the SERVER'S
// number — it is Waslo's current understanding of WhatsApp's per-screen limit rather than a
// confirmed one, so it must never be typed on this side.
const fieldCountLine = computed(() =>
  props.form?.maxFields
    ? t('WASLO.FORMS.TASKS.FIELD_COUNT_CAPPED', {
        count: fields.value.length,
        max: props.form.maxFields,
      })
    : t('WASLO.FORMS.TASKS.FIELD_COUNT', { count: fields.value.length })
);

/** P-4.10: "{questions} on {pages}", once the preview has said how the form is split. */
const pagesLine = computed(() => {
  const pages = props.form?.pages ?? [];
  if (pages.length === 0) return '';
  const questions = pages.reduce(
    (count, page) => count + page.fieldNames.length,
    0
  );
  return t('WASLO.FORMS.ARRANGE.SUMMARY', {
    questions: t(
      'WASLO.FORMS.ARRANGE.QUESTIONS',
      { count: format.value.format(questions) },
      questions
    ),
    pages: t(
      'WASLO.FORMS.ARRANGE.PAGES',
      { count: format.value.format(pages.length) },
      pages.length
    ),
  });
});

/** Nothing is live yet: the first publish is the row's own button, as it always was. */
const firstPublish = computed(
  () => props.standing !== null && nothingPublished(props.standing)
);
/** Something is recorded: one line per language, each with its own verb. */
const showsLines = computed(
  () => props.standing !== null && !nothingPublished(props.standing)
);

/** Whether any Publish or Update button is on this row right now. */
const offersPublish = computed(() => {
  if (isOpen.value && props.form?.hasForm && firstPublish.value) return true;
  if (!showsLines.value) return false;
  return FORM_LANGS.some(lang => {
    const state = props.standing[lang]?.state;
    return isStale(state) || state === FORM_STATE.NOT_PUBLISHED;
  });
});

/** The reason beside a Publish button that is off, or '' when there is none to give. */
const blockedLine = computed(() =>
  !props.canPublish && props.blockedReason && offersPublish.value
    ? props.blockedReason
    : ''
);

/** One literal key per language and per state, so the translation checker sees each one. */
const LANGUAGE_WORD = {
  en: () => t('WASLO.FORMS.LANG.EN'),
  ar: () => t('WASLO.FORMS.LANG.AR'),
};
const STATE_WORD = {
  [FORM_STATE.LIVE]: () => t('WASLO.FORMS.TASKS.STATE.LIVE'),
  [FORM_STATE.WORDS_CHANGED]: () => t('WASLO.FORMS.TASKS.STATE.WORDS_CHANGED'),
  [FORM_STATE.QUESTIONS_CHANGED]: () =>
    t('WASLO.FORMS.TASKS.STATE.QUESTIONS_CHANGED'),
  [FORM_STATE.NOT_PUBLISHED]: () => t('WASLO.FORMS.TASKS.STATE.NOT_PUBLISHED'),
};

const lineFor = lang =>
  t('WASLO.FORMS.TASKS.STATE.LINE', {
    language: LANGUAGE_WORD[lang](),
    state: STATE_WORD[props.standing[lang].state](),
  });
</script>

<template>
  <li
    class="flex flex-col gap-4 px-5 py-4 list-none outline outline-1 -outline-offset-1 rounded-xl outline-n-container bg-n-solid-1"
    :data-waslo-forms-task="task.id"
  >
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <p class="mb-0 text-sm font-medium truncate text-n-slate-12">
          {{ task.title }}
        </p>
        <p
          v-if="isOpen"
          class="mb-0 text-xs leading-5 text-n-slate-10"
          data-waslo-forms-task-count
        >
          {{ fieldCountLine }}
        </p>
        <p
          v-if="isOpen && pagesLine"
          class="mb-0 text-xs leading-5 text-n-slate-10"
          data-waslo-forms-task-pages
        >
          {{ pagesLine }}
        </p>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <!-- P-4.10: the form's pages, its words and which questions it carries. -->
        <Button
          :label="t('WASLO.FORMS.ARRANGE.OPEN')"
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-forms-arrange
          @click="emit('arrange')"
        />
        <Button
          :label="
            isOpen
              ? t('WASLO.FORMS.TASKS.HIDE')
              : isPreviewing
                ? t('WASLO.FORMS.TASKS.PREVIEWING')
                : t('WASLO.FORMS.TASKS.PREVIEW')
          "
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="isPreviewing"
          :disabled="isPreviewing"
          data-waslo-forms-preview
          @click="isOpen ? emit('hide') : emit('preview')"
        />
        <Button
          v-if="isOpen && form.hasForm && firstPublish"
          :label="
            isPublishing
              ? t('WASLO.FORMS.TASKS.PUBLISHING')
              : t('WASLO.FORMS.TASKS.PUBLISH')
          "
          size="sm"
          :is-loading="isPublishing"
          :disabled="isPublishing || !canPublish"
          data-waslo-forms-publish
          @click="emit('publish', FORM_LANGS)"
        />
      </div>
    </div>

    <p
      v-if="blockedLine"
      class="mb-0 text-sm leading-6 text-n-amber-11"
      data-waslo-forms-publish-blocked
    >
      {{ blockedLine }}
    </p>

    <p
      v-if="errorMessage"
      class="mb-0 text-sm leading-6 text-n-ruby-9"
      data-waslo-forms-task-error
    >
      {{ errorMessage }}
    </p>

    <p
      v-if="publishedNote"
      class="mb-0 text-sm leading-6 text-n-teal-11"
      data-waslo-forms-task-published
    >
      {{ publishedNote }}
    </p>

    <p
      v-if="standing === null"
      class="mb-0 text-sm leading-6 text-n-slate-11"
      data-waslo-forms-task-unknown
    >
      {{ t('WASLO.FORMS.TASKS.STATE.UNKNOWN') }}
    </p>

    <!-- P-4.8: one line per language, once anything is published. -->
    <ul
      v-else-if="showsLines"
      class="flex flex-col gap-2 mb-0 list-none"
      data-waslo-forms-task-standing
    >
      <li
        v-for="lang in FORM_LANGS"
        :key="lang"
        class="flex flex-wrap items-center gap-2 text-sm leading-6"
        :data-waslo-forms-lang-state="lang"
        :data-waslo-forms-state="standing[lang].state"
      >
        <span class="text-n-slate-12" dir="auto">{{ lineFor(lang) }}</span>
        <Button
          v-if="
            standing[lang].state === FORM_STATE.LIVE && standing[lang].flowId
          "
          :label="
            tryingLang === lang
              ? t('WASLO.FORMS.TASKS.STATE.TRYING')
              : t('WASLO.FORMS.TASKS.STATE.TRY')
          "
          variant="faded"
          color="slate"
          size="sm"
          :disabled="tryingLang !== ''"
          data-waslo-forms-try
          @click="emit('try', lang, standing[lang].flowId)"
        />
        <Button
          v-else-if="isStale(standing[lang].state)"
          :label="t('WASLO.FORMS.TASKS.STATE.UPDATE')"
          size="sm"
          :is-loading="isPublishing"
          :disabled="isPublishing || !canPublish"
          data-waslo-forms-update
          @click="emit('publish', [lang])"
        />
        <Button
          v-else-if="standing[lang].state === FORM_STATE.NOT_PUBLISHED"
          :label="t('WASLO.FORMS.TASKS.STATE.PUBLISH')"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="isPublishing || !canPublish"
          data-waslo-forms-publish-lang
          @click="emit('publish', [lang])"
        />
      </li>
    </ul>

    <div v-if="isOpen" class="grid gap-4 sm:grid-cols-2">
      <section class="flex flex-col gap-2" data-waslo-forms-fields>
        <p class="mb-0 text-xs font-medium uppercase text-n-slate-10">
          {{ t('WASLO.FORMS.TASKS.FIELDS_HEADING') }}
        </p>
        <p
          v-if="fields.length === 0"
          class="mb-0 text-sm leading-6 text-n-slate-11"
        >
          {{ t('WASLO.FORMS.TASKS.NO_FIELDS') }}
        </p>
        <ul v-else class="flex flex-col gap-1 mb-0 list-none">
          <li
            v-for="field in fields"
            :key="field.name"
            class="text-sm leading-6 text-n-slate-12"
          >
            {{ fieldLine(field) }}
          </li>
        </ul>
      </section>

      <section class="flex flex-col gap-2" data-waslo-forms-stays>
        <p class="mb-0 text-xs font-medium uppercase text-n-slate-10">
          {{ t('WASLO.FORMS.TASKS.STAYS_HEADING') }}
        </p>
        <p class="mb-0 text-xs leading-5 text-n-slate-10">
          {{ t('WASLO.FORMS.TASKS.STAYS_NOTE') }}
        </p>
        <ul v-if="stays.length" class="flex flex-col gap-1 mb-0 list-none">
          <li
            v-for="step in stays"
            :key="step.stepId ?? step.name"
            class="text-sm leading-6 text-n-slate-11"
          >
            {{
              t('WASLO.FORMS.TASKS.STEP_SHAPE', {
                name: step.name,
                shape: stayReason(step.reason),
              })
            }}
          </li>
        </ul>
      </section>
    </div>
  </li>
</template>
